import { useState, useEffect, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, NavLink, useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  ShieldAlert, CheckCircle2, Utensils, Users, Search, Filter, X, 
  RefreshCw, Settings, FileText, ChevronRight, ChevronDown, ChevronUp, ChevronLeft, AlertTriangle, Mail,
  Calendar, Clock, User, LayoutDashboard, Menu, Printer
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============ CONTEXT ============
const AppContext = createContext();

export const useApp = () => useContext(AppContext);

const AppProvider = ({ children }) => {
  const [role, setRole] = useState(() => {
    // Persist role in localStorage
    const savedRole = localStorage.getItem('allergyCheckRole');
    return savedRole || 'staff';
  });
  const [groups, setGroups] = useState([]);
  const [allergens, setAllergens] = useState([]);
  const [stats, setStats] = useState({});
  const [config, setConfig] = useState({ cross_contamination_keywords: [] });
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');

  // Save role to localStorage when it changes
  const handleSetRole = (newRole) => {
    localStorage.setItem('allergyCheckRole', newRole);
    setRole(newRole);
  };

  const fetchGroups = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/groups`);
      setGroups(res.data);
    } catch (e) {
      console.error("Failed to fetch groups", e);
    }
  }, []);

  const fetchAllergens = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/allergens`);
      setAllergens(res.data);
    } catch (e) {
      console.error("Failed to fetch allergens", e);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/stats`);
      setStats(res.data);
      setLastSync(res.data.last_sync);
    } catch (e) {
      console.error("Failed to fetch stats", e);
    }
  }, []);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/config`);
      setConfig(res.data);
    } catch (e) {
      console.error("Failed to fetch config", e);
    }
  }, []);

  const syncData = async (date) => {
    setSyncing(true);
    try {
      const syncDate = date || selectedDate || '';
      const params = syncDate ? `?date=${syncDate}` : '';
      const res = await axios.post(`${API}/snapshot/sync${params}`);
      if (res.data.success) {
        if (res.data.is_camp_day === false) {
          toast.info(res.data.message);
        } else {
          toast.success(`Roster synced: ${res.data.campers_synced} campers in ${res.data.groups_synced} groups`);
        }
        setLastSync(res.data.last_sync);
        await fetchGroups();
        await fetchAllergens();
        await fetchStats();
      } else {
        toast.error(`Sync failed: ${res.data.message}`);
      }
    } catch (e) {
      toast.error("Roster sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const syncAllergies = async () => {
    setSyncing(true);
    try {
      toast.info("Syncing allergy data from Google Sheets...");
      const res = await axios.post(`${API}/sync-allergies`);
      if (res.data.success) {
        toast.success(`Allergy sync completed! ${res.data.with_allergies_count} campers with allergies found.`);
        await fetchStats();
      } else {
        toast.error(`Allergy sync failed: ${res.data.message}`);
      }
    } catch (e) {
      toast.error("Allergy sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const seedDatabase = async () => {
    try {
      const res = await axios.post(`${API}/seed`);
      if (res.data.success) {
        toast.success("Database seeded successfully");
        await fetchGroups();
        await fetchAllergens();
        await fetchStats();
      }
    } catch (e) {
      toast.error("Failed to seed database");
    }
  };

  useEffect(() => {
    fetchGroups();
    fetchAllergens();
    fetchStats();
    fetchConfig();
  }, [fetchGroups, fetchAllergens, fetchStats, fetchConfig]);

  return (
    <AppContext.Provider value={{
      role, setRole: handleSetRole, groups, allergens, stats, config, syncing, lastSync,
      selectedDate, setSelectedDate,
      syncData, syncAllergies, seedDatabase, fetchGroups, fetchAllergens, fetchStats, fetchConfig
    }}>
      {children}
    </AppContext.Provider>
  );
};

// ============ LOGO COMPONENT ============
const Logo = () => (
  <img 
    src="https://customer-assets.emergentagent.com/job_rrdcallergyapp/artifacts/f1lb0gpv_Riverview%20Athletics%20Logo.png"
    alt="Rolling River Day Camp"
    className="h-12 w-12 object-contain"
    data-testid="camp-logo"
  />
);

// ============ HEADER ============
const Header = () => {
  const { role, setRole, syncing, syncData, syncAllergies, lastSync } = useApp();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="sticky-header px-4 py-3" data-testid="main-header">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between">
          {/* Logo and Title */}
          <div className="flex items-center gap-3">
            <Logo />
            <div className="hidden sm:block">
              <h1 className="text-xl font-bold text-[#2851A3]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Rolling River
              </h1>
              <p className="text-xs text-slate-500 font-medium">Allergy Check</p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-2" data-testid="desktop-nav">
            <NavLink to="/" className={({ isActive }) => `nav-link ${isActive ? 'nav-link-active' : ''}`} end>
              <span className="flex items-center gap-2"><Utensils size={16} /> Today</span>
            </NavLink>
            <NavLink to="/allergens" className={({ isActive }) => `nav-link ${isActive ? 'nav-link-active' : ''}`}>
              <span className="flex items-center gap-2"><ShieldAlert size={16} /> Allergens</span>
            </NavLink>
            <NavLink to="/groups" className={({ isActive }) => `nav-link ${isActive ? 'nav-link-active' : ''}`}>
              <span className="flex items-center gap-2"><Users size={16} /> Groups</span>
            </NavLink>
            <NavLink to="/report" className={({ isActive }) => `nav-link ${isActive ? 'nav-link-active' : ''}`}>
              <span className="flex items-center gap-2"><Printer size={16} /> Report</span>
            </NavLink>
            {role === "admin" && (
              <>
                <NavLink to="/admin" className={({ isActive }) => `nav-link ${isActive ? 'nav-link-active' : ''}`}>
                  <span className="flex items-center gap-2"><Settings size={16} /> Admin</span>
                </NavLink>
                <NavLink to="/audit" className={({ isActive }) => `nav-link ${isActive ? 'nav-link-active' : ''}`}>
                  <span className="flex items-center gap-2"><FileText size={16} /> Audit</span>
                </NavLink>
              </>
            )}
          </nav>

          {/* Right side controls */}
          <div className="flex items-center gap-3">
            {/* Sync Button */}
            <div className="hidden sm:flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => syncData()} 
                disabled={syncing}
                className="flex items-center gap-2 rounded-full border-[#2851A3] text-[#2851A3] hover:bg-blue-50"
                data-testid="sync-button"
              >
                <RefreshCw size={16} className={syncing ? "animate-spin" : ""} />
                {syncing ? "Syncing..." : "Sync Roster"}
              </Button>
            </div>

            {/* Role Selector */}
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="w-[110px] rounded-full" data-testid="role-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="staff">Staff</SelectItem>
                <SelectItem value="nurse">Nurse</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>

            {/* Mobile menu button */}
            <Button 
              variant="ghost" 
              size="icon" 
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              <Menu size={24} />
            </Button>
          </div>
        </div>

        {/* Last sync info */}
        {lastSync && (
          <p className="text-xs text-slate-400 mt-1 hidden sm:block">
            Last sync: {new Date(lastSync).toLocaleString()}
          </p>
        )}
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 bg-white border-b shadow-lg z-50 p-4">
          <nav className="flex flex-col gap-2">
            <NavLink to="/" className={({ isActive }) => `nav-link ${isActive ? 'nav-link-active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
              <Utensils size={16} /> Today
            </NavLink>
            <NavLink to="/allergens" className={({ isActive }) => `nav-link ${isActive ? 'nav-link-active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
              <ShieldAlert size={16} /> Allergens
            </NavLink>
            <NavLink to="/groups" className={({ isActive }) => `nav-link ${isActive ? 'nav-link-active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
              <Users size={16} /> Groups
            </NavLink>
            {role === "admin" && (
              <>
                <NavLink to="/admin" className={({ isActive }) => `nav-link ${isActive ? 'nav-link-active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                  <Settings size={16} /> Admin
                </NavLink>
                <NavLink to="/audit" className={({ isActive }) => `nav-link ${isActive ? 'nav-link-active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                  <FileText size={16} /> Audit
                </NavLink>
              </>
            )}
            <div className="flex flex-col gap-2 mt-2">
              <Button onClick={() => { syncData(); setMobileMenuOpen(false); }} disabled={syncing} className="btn-primary">
                <RefreshCw size={16} className={syncing ? "animate-spin" : ""} />
                {syncing ? "Syncing..." : "Sync Roster"}
              </Button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

// ============ MOBILE BOTTOM NAV ============
const MobileNav = () => {
  const { role } = useApp();
  
  return (
    <div className="mobile-nav md:hidden" data-testid="mobile-bottom-nav">
      <div className="flex justify-around">
        <NavLink to="/" className={({ isActive }) => `mobile-nav-item ${isActive ? 'mobile-nav-item-active' : ''}`} end>
          <Utensils size={20} />
          <span className="text-xs mt-1">Today</span>
        </NavLink>
        <NavLink to="/allergens" className={({ isActive }) => `mobile-nav-item ${isActive ? 'mobile-nav-item-active' : ''}`}>
          <ShieldAlert size={20} />
          <span className="text-xs mt-1">Allergens</span>
        </NavLink>
        <NavLink to="/groups" className={({ isActive }) => `mobile-nav-item ${isActive ? 'mobile-nav-item-active' : ''}`}>
          <Users size={20} />
          <span className="text-xs mt-1">Groups</span>
        </NavLink>
        <NavLink to="/report" className={({ isActive }) => `mobile-nav-item ${isActive ? 'mobile-nav-item-active' : ''}`}>
          <Printer size={20} />
          <span className="text-xs mt-1">Report</span>
        </NavLink>
        {role === "admin" && (
          <NavLink to="/admin" className={({ isActive }) => `mobile-nav-item ${isActive ? 'mobile-nav-item-active' : ''}`}>
            <Settings size={20} />
            <span className="text-xs mt-1">Admin</span>
          </NavLink>
        )}
      </div>
    </div>
  );
};

// ============ ALLERGY CHIP ============
const AllergyChip = ({ allergen, size = "sm" }) => {
  const severeAllergens = ["peanut", "tree nut", "shellfish", "fish", "egg", "milk", "dairy", "sesame", "wheat", "soy"];
  const isSevere = severeAllergens.some(a => allergen.toLowerCase().includes(a));
  
  return (
    <span 
      className={`allergy-chip ${isSevere ? 'allergy-chip-severe' : 'allergy-chip-moderate'} ${size === 'lg' ? 'text-sm px-3 py-1.5' : ''}`}
      data-testid={`allergy-chip-${allergen.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {allergen}
    </span>
  );
};

// ============ CAMPER CARD ============
const CamperCard = ({ camper, checklistItem, onCheck, onClick }) => {
  const { role } = useApp();
  const isChecked = checklistItem?.is_checked || false;
  const lunchOrder = checklistItem?.lunch_order;
  
  return (
    <div 
      className={`card-hover p-4 bg-white border rounded-xl flex items-center gap-4 ${isChecked ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-100'}`}
      data-testid={`camper-card-${camper.id}`}
    >
      {/* Large Checkbox */}
      <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
        <Checkbox 
          checked={isChecked}
          onCheckedChange={(checked) => onCheck(camper.id, checked)}
          className="checkbox-lg"
          data-testid={`checkbox-${camper.id}`}
        />
      </div>

      {/* Camper Info */}
      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onClick(camper)}>
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-semibold text-slate-800">
            {camper.first_name} {camper.last_name}
          </h3>
          {camper.is_cross_contamination_sensitive && (
            <span className="allergy-chip allergy-chip-cc" data-testid={`cc-badge-${camper.id}`}>
              <AlertTriangle size={12} className="mr-1" /> CC
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
          <span className="group-badge">{camper.group_name}</span>
          {lunchOrder && (
            <span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full flex items-center gap-1" data-testid={`lunch-order-${camper.id}`}>
              <Utensils size={10} /> {lunchOrder}
            </span>
          )}
        </div>
        {camper.allergen_tags && camper.allergen_tags.length > 0 ? (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {camper.allergen_tags.slice(0, 4).map((a, i) => (
              <AllergyChip key={i} allergen={a} />
            ))}
            {camper.allergen_tags.length > 4 && (
              <span className="text-xs text-slate-400 self-center">+{camper.allergen_tags.length - 4} more</span>
            )}
          </div>
        ) : (
          <span className="allergy-chip allergy-chip-safe mt-2">
            <CheckCircle2 size={12} className="mr-1" /> No Known Allergies
          </span>
        )}
      </div>

      {/* Status indicator */}
      <div className="flex-shrink-0 flex flex-col items-end gap-1">
        <div className={`status-dot ${isChecked ? 'status-checked' : 'status-unchecked'}`} />
        {isChecked && checklistItem?.checked_at && (
          <span className="text-xs text-slate-400">
            {new Date(checklistItem.checked_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
      </div>

      <ChevronRight size={20} className="text-slate-300 flex-shrink-0" />
    </div>
  );
};

// ============ TODAY PAGE ============
const TodayPage = () => {
  const { role, groups, allergens, stats, seedDatabase, syncing, syncData, selectedDate, setSelectedDate } = useApp();
  const [checklist, setChecklist] = useState({ items: [], total: 0, checked: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedAllergen, setSelectedAllergen] = useState("");
  const [ccOnly, setCcOnly] = useState(false);
  const [selectedCamper, setSelectedCamper] = useState(null);
  const [staffNote, setStaffNote] = useState("");
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [collapsedPeriods, setCollapsedPeriods] = useState({});
  const navigate = useNavigate();

  // Restore scroll position on mount
  useEffect(() => {
    const savedScroll = sessionStorage.getItem('todayPageScroll');
    if (savedScroll) {
      setTimeout(() => {
        window.scrollTo(0, parseInt(savedScroll, 10));
        sessionStorage.removeItem('todayPageScroll');
      }, 100);
    }
  }, []);

  // Save scroll position before navigating to camper detail
  const handleCamperClick = (camper) => {
    sessionStorage.setItem('todayPageScroll', window.scrollY.toString());
    navigate(`/camper/${camper.id}`);
  };

  const fetchChecklist = useCallback(async () => {
    try {
      const params = selectedDate ? `?date=${selectedDate}` : '';
      const res = await axios.get(`${API}/checklist/today${params}`);
      setChecklist(res.data);
    } catch (e) {
      console.error("Failed to fetch checklist", e);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchChecklist();
  }, [fetchChecklist]);

  // When date changes, sync and refresh
  const handleDateChange = async (newDate) => {
    setSelectedDate(newDate);
    setLoading(true);
    await syncData(newDate);
    try {
      const res = await axios.get(`${API}/checklist/today?date=${newDate}`);
      setChecklist(res.data);
    } catch (e) {
      console.error("Failed to fetch checklist for date", e);
    } finally {
      setLoading(false);
    }
  };

  const handleCheck = async (camperId, isChecked) => {
    try {
      await axios.post(`${API}/checklist/check`, {
        camper_id: camperId,
        is_checked: isChecked,
        checked_by: role,
        staff_note: staffNote,
        date: selectedDate || undefined
      });
      setStaffNote("");
      await fetchChecklist();
      toast.success(isChecked ? "Camper checked" : "Camper unchecked");
    } catch (e) {
      toast.error("Failed to update checklist");
    }
  };

  const handleReset = async () => {
    try {
      await axios.post(`${API}/checklist/reset?user=${role}`);
      await fetchChecklist();
      toast.success("Checklist reset for today");
    } catch (e) {
      toast.error("Failed to reset checklist");
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedGroup("");
    setSelectedAllergen("");
    setCcOnly(false);
  };

  // Filter items
  const filteredItems = checklist.items.filter(item => {
    const camper = item.camper;
    if (!camper) return false;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const fullName = `${camper.first_name} ${camper.last_name}`.toLowerCase();
      if (!fullName.includes(term)) return false;
    }

    if (selectedGroup && camper.group_id !== selectedGroup) return false;

    if (selectedAllergen) {
      const hasAllergen = camper.allergen_tags?.some(a => 
        a.toLowerCase().includes(selectedAllergen.toLowerCase())
      );
      if (!hasAllergen) return false;
    }

    if (ccOnly && !camper.is_cross_contamination_sensitive) return false;

    return true;
  });

  // Sort items alphabetically by last name, then first name
  const sortedItems = [...filteredItems].sort((a, b) => {
    const lastNameA = (a.camper?.last_name || '').toLowerCase();
    const lastNameB = (b.camper?.last_name || '').toLowerCase();
    if (lastNameA !== lastNameB) return lastNameA.localeCompare(lastNameB);
    const firstNameA = (a.camper?.first_name || '').toLowerCase();
    const firstNameB = (b.camper?.first_name || '').toLowerCase();
    return firstNameA.localeCompare(firstNameB);
  });

  // Lunch period schedule mapping (all groups assigned except N13 which has no period)
  const lunchPeriods = {
    "10:50-11:20": ["b12", "b13", "b14", "b15", "g12", "g13", "g14", "g15"],
    "11:20-11:50": ["g10", "g11", "b10", "b11", "n2", "n7", "n8"],
    "11:50-12:20": ["g1", "g2", "g3", "g9", "b8", "b9", "g8", "n3", "n4", "n5"],
    "12:20-12:50": ["b1", "b2", "b3", "b4", "g4", "n10", "n11", "n12"],
    "12:50-1:20": ["g5", "g6", "g7", "b5", "b6", "b7", "n14", "n15", "n16"]
  };

  // Extract group code from group name (e.g., "B01 Lobsters" -> "b1", "G12 Dolphins" -> "g12")
  const getGroupCode = (groupName) => {
    if (!groupName) return "";
    const match = groupName.match(/^([BGNbgn])(\d+)/i);
    if (match) {
      const letter = match[1].toLowerCase();
      const num = parseInt(match[2], 10); // Remove leading zeros
      return `${letter}${num}`;
    }
    return "";
  };

  // Get lunch period for a group
  const getLunchPeriod = (groupName) => {
    if (!groupName) return "Other"; // Unassigned campers go to Other
    const code = getGroupCode(groupName);
    for (const [period, groups] of Object.entries(lunchPeriods)) {
      if (groups.includes(code)) {
        return period;
      }
    }
    return "Other"; // Groups not in any period go to Other
  };

  // Group items by group_name
  const groupedItems = sortedItems.reduce((acc, item) => {
    const groupName = item.camper?.group_name || 'Unassigned';
    if (!acc[groupName]) {
      acc[groupName] = [];
    }
    acc[groupName].push(item);
    return acc;
  }, {});

  // Sort group names by lunch period, then alphabetically within period
  const sortedGroupNames = Object.keys(groupedItems).sort((a, b) => {
    const periodA = getLunchPeriod(a);
    const periodB = getLunchPeriod(b);
    if (periodA !== periodB) {
      // Put "Other" at the end
      if (periodA === "Other") return 1;
      if (periodB === "Other") return -1;
      return periodA.localeCompare(periodB);
    }
    return a.localeCompare(b);
  });

  // Group the sorted groups by lunch period for section headers
  const groupsByPeriod = {};
  sortedGroupNames.forEach(groupName => {
    const period = getLunchPeriod(groupName);
    if (!groupsByPeriod[period]) {
      groupsByPeriod[period] = [];
    }
    groupsByPeriod[period].push(groupName);
  });

  const periodOrder = ["10:50-11:20", "11:20-11:50", "11:50-12:20", "12:20-12:50", "12:50-1:20", "Other"];
  const sortedPeriods = Object.keys(groupsByPeriod).sort((a, b) => {
    return periodOrder.indexOf(a) - periodOrder.indexOf(b);
  });

  const checkedCount = filteredItems.filter(i => i.is_checked).length;
  const hasFilters = searchTerm || selectedGroup || selectedAllergen || ccOnly;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-[#2851A3]" size={32} />
      </div>
    );
  }

  // Show sync prompt if no data
  if (checklist.items.length === 0) {
    return (
      <div className="max-w-xl mx-auto p-8 text-center">
        <div className="bg-white rounded-2xl border p-8 shadow-sm">
          <ShieldAlert size={48} className="mx-auto text-[#2851A3] mb-4" />
          <h2 className="text-2xl font-bold text-slate-800 mb-2">No Roster Data</h2>
          <p className="text-slate-500 mb-4">Select a camp date and sync the roster from Camper Snapshot.</p>
          <div className="flex flex-col items-center gap-4">
            <Input
              type="date"
              value={selectedDate || ''}
              onChange={(e) => setSelectedDate(e.target.value)}
              min="2026-06-29"
              max="2026-08-20"
              className="w-[200px]"
              data-testid="empty-date-picker"
            />
            <Button 
              onClick={() => syncData(selectedDate)} 
              disabled={syncing || !selectedDate}
              className="btn-primary" 
              data-testid="sync-roster-btn"
            >
              <RefreshCw size={16} className={syncing ? "animate-spin" : ""} />
              {syncing ? "Syncing..." : "Sync Roster"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 pb-24 md:pb-6" data-testid="today-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#2851A3]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Calendar className="inline mr-2" size={28} />
              Daily Roster
            </h1>
            <p className="text-slate-500 mt-1">
              {checkedCount} of {filteredItems.length} campers checked
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Date Picker */}
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={selectedDate || checklist.date || ''}
                onChange={(e) => handleDateChange(e.target.value)}
                min="2026-06-29"
                max="2026-08-20"
                className="w-[165px] rounded-full text-sm"
                data-testid="date-picker"
              />
            </div>
            
            {role === "admin" && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid="reset-checklist-btn">
                    Reset Today
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Reset Today's Checklist?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will uncheck all campers for today. This action cannot be undone. History will be preserved.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleReset} className="bg-red-600 hover:bg-red-700">
                      Reset
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6" data-testid="filters-card">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input 
                placeholder="Search camper..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
                data-testid="search-camper-input"
              />
            </div>

            {/* Group Filter */}
            <Select value={selectedGroup || "all"} onValueChange={(v) => setSelectedGroup(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="group-filter">
                <SelectValue placeholder="All Groups" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Groups</SelectItem>
                {groups.map(g => (
                  <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Allergen Filter */}
            <Select value={selectedAllergen || "all"} onValueChange={(v) => setSelectedAllergen(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="allergen-filter">
                <SelectValue placeholder="All Allergens" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Allergens</SelectItem>
                {allergens.slice(0, 20).map(a => (
                  <SelectItem key={a.id} value={a.name}>{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* CC Toggle */}
            <div className="flex items-center gap-3 justify-between md:justify-start">
              <Label htmlFor="cc-toggle" className="text-sm text-slate-600">CC Only</Label>
              <Switch 
                id="cc-toggle" 
                checked={ccOnly} 
                onCheckedChange={setCcOnly}
                data-testid="cc-toggle"
              />
            </div>
          </div>

          {hasFilters && (
            <Button variant="ghost" onClick={clearFilters} className="mt-3 text-slate-500" data-testid="clear-filters-btn">
              <X size={16} className="mr-1" /> Clear filters
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Results count */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500">
          Showing {filteredItems.length} camper{filteredItems.length !== 1 ? 's' : ''} in {sortedGroupNames.length} group{sortedGroupNames.length !== 1 ? 's' : ''}
        </p>
        <div className="flex items-center gap-2">
          <span className="status-dot status-checked" />
          <span className="text-xs text-slate-500">{checkedCount} checked</span>
        </div>
      </div>

      {/* Collapse/Expand All Button */}
      <div className="flex justify-end mb-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            const allCollapsed = sortedGroupNames.every(g => collapsedGroups[g]);
            const newState = {};
            sortedGroupNames.forEach(g => { newState[g] = !allCollapsed; });
            setCollapsedGroups(newState);
          }}
          className="text-xs text-slate-500 hover:text-[#2851A3]"
        >
          {sortedGroupNames.every(g => collapsedGroups[g]) ? (
            <><ChevronDown size={14} className="mr-1" /> Expand All</>
          ) : (
            <><ChevronUp size={14} className="mr-1" /> Collapse All</>
          )}
        </Button>
      </div>

      {/* Camper List - Grouped by Lunch Period and Group */}
      <div className="space-y-4" data-testid="camper-list">
        {sortedPeriods.map(period => {
          const isPeriodCollapsed = collapsedPeriods[period];
          const periodGroups = groupsByPeriod[period];
          const periodCamperCount = periodGroups.reduce((sum, g) => sum + (groupedItems[g]?.length || 0), 0);
          const periodCheckedCount = periodGroups.reduce((sum, g) => sum + (groupedItems[g]?.filter(i => i.is_checked).length || 0), 0);
          const periodHasAllergies = periodGroups.some(g => groupedItems[g]?.some(i => i.camper?.allergen_tags?.length > 0));
          
          return (
            <div key={period} className="space-y-2">
              {/* Lunch Period Header - Clickable to toggle */}
              <div 
                onClick={() => setCollapsedPeriods(prev => ({ ...prev, [period]: !prev[period] }))}
                className="sticky top-[72px] z-20 bg-[#2851A3] text-white py-3 px-4 rounded-lg shadow-md flex items-center justify-between cursor-pointer hover:bg-[#1e3d7a] transition-all"
                data-testid={`period-header-${period}`}
              >
                <div className="flex items-center gap-3">
                  {isPeriodCollapsed ? (
                    <ChevronRight size={22} className="text-white" />
                  ) : (
                    <ChevronDown size={22} className="text-white" />
                  )}
                  <Clock size={18} />
                  <h2 className="font-bold text-lg">Lunch: {period}</h2>
                  {periodHasAllergies && (
                    <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded-full flex items-center gap-1">
                      <ShieldAlert size={12} /> Allergies
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm opacity-90">
                    {periodGroups.length} group{periodGroups.length !== 1 ? 's' : ''}
                  </span>
                  <div className="flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full">
                    <div className={`w-2 h-2 rounded-full ${periodCheckedCount === periodCamperCount ? 'bg-green-400' : periodCheckedCount > 0 ? 'bg-yellow-400' : 'bg-white/50'}`} />
                    <span className="text-sm font-medium">{periodCheckedCount}/{periodCamperCount}</span>
                  </div>
                </div>
              </div>
              
              {/* Groups in this lunch period as clickable cards */}
              {!isPeriodCollapsed && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pl-1">
                  {periodGroups.map(groupName => {
                    const groupItems = groupedItems[groupName];
                    const groupCheckedCount = groupItems.filter(i => i.is_checked).length;
                    const hasAllergies = groupItems.some(i => i.camper?.allergen_tags?.length > 0);
                    const allergyCampers = groupItems.filter(i => i.camper?.allergen_tags?.length > 0);
                    const group = groups.find(g => g.name === groupName);
                    
                    return (
                      <div 
                        key={groupName}
                        onClick={() => {
                          sessionStorage.setItem('todayPageScroll', window.scrollY.toString());
                          navigate(`/group/${group?.id || groupName}`, { state: { groupName, groupItems, selectedDate } });
                        }}
                        className={`bg-white rounded-xl border-2 p-4 cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02] hover:border-[#2851A3]
                          ${hasAllergies ? 'border-l-4 border-l-red-400' : 'border-slate-200'}
                        `}
                        data-testid={`group-card-${groupName}`}
                      >
                        {/* Group Name */}
                        <div className="flex items-center gap-2 mb-3">
                          <Users size={20} className="text-[#2851A3]" />
                          <h3 className="font-bold text-[#2851A3] text-lg">{groupName}</h3>
                        </div>
                        
                        {/* Stats Row */}
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm text-slate-600">
                            {groupItems.length} campers
                          </span>
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${groupCheckedCount === groupItems.length ? 'bg-green-500' : groupCheckedCount > 0 ? 'bg-yellow-500' : 'bg-slate-300'}`} />
                            <span className={`text-sm font-bold ${groupCheckedCount === groupItems.length ? 'text-green-600' : 'text-slate-700'}`}>
                              {groupCheckedCount}/{groupItems.length}
                            </span>
                          </div>
                        </div>
                        
                        {/* Allergy Info */}
                        {hasAllergies ? (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-2">
                            <div className="flex items-center gap-1 text-red-600 text-xs font-semibold mb-1">
                              <ShieldAlert size={14} />
                              {allergyCampers.length} with allergies
                            </div>
                            <div className="text-xs text-red-700 truncate">
                              {allergyCampers.slice(0, 2).map(i => i.camper?.first_name).join(', ')}
                              {allergyCampers.length > 2 && ` +${allergyCampers.length - 2} more`}
                            </div>
                          </div>
                        ) : (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-2">
                            <div className="flex items-center gap-1 text-green-600 text-xs font-semibold">
                              <CheckCircle2 size={14} />
                              No allergies
                            </div>
                          </div>
                        )}
                        
                        {/* Click indicator */}
                        <div className="flex items-center justify-end mt-3 text-slate-400">
                          <span className="text-xs mr-1">View campers</span>
                          <ChevronRight size={16} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filteredItems.length === 0 && (
        <div className="text-center py-12">
          <ShieldAlert size={48} className="mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500">No campers match your filters</p>
        </div>
      )}
    </div>
  );
};

// ============ GROUP DETAIL PAGE ============
const GroupDetailPage = () => {
  const { role, groups, selectedDate } = useApp();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [groupData, setGroupData] = useState(null);
  const [campers, setCampers] = useState([]);
  const [checklist, setChecklist] = useState({});
  const [loading, setLoading] = useState(true);

  // Get group name and date from location state
  const groupName = location.state?.groupName;
  const dateFromState = location.state?.selectedDate || selectedDate;

  useEffect(() => {
    const fetchGroupData = async () => {
      try {
        // Fetch group info
        const groupRes = await axios.get(`${API}/groups`);
        const group = groupRes.data.find(g => g.id === id || g.name === id);
        setGroupData(group);

        // Fetch checklist for the selected date
        const dateParam = dateFromState ? `?date=${dateFromState}` : '';
        const checklistRes = await axios.get(`${API}/checklist/today${dateParam}`);
        const items = checklistRes.data.items || [];
        
        // Filter to only campers in this group
        const targetGroupName = group?.name || groupName || id;
        const isUnassigned = targetGroupName === 'Unassigned' || id === 'Unassigned';
        
        const groupCampers = items.filter(item => {
          if (isUnassigned) {
            return !item.camper?.group_name || item.camper?.group_name === '' || item.camper?.group_name === 'Unassigned';
          }
          return item.camper?.group_name === targetGroupName;
        });
        
        // Sort alphabetically by last name
        groupCampers.sort((a, b) => {
          const lastA = (a.camper?.last_name || '').toLowerCase();
          const lastB = (b.camper?.last_name || '').toLowerCase();
          return lastA.localeCompare(lastB);
        });
        
        setCampers(groupCampers);
        
        // Build checklist map
        const checkMap = {};
        groupCampers.forEach(item => {
          checkMap[item.camper_id] = item;
        });
        setChecklist(checkMap);
      } catch (e) {
        console.error("Failed to fetch group data", e);
      } finally {
        setLoading(false);
      }
    };
    fetchGroupData();
  }, [id, groupName, dateFromState]);

  const handleCheck = async (camperId, isChecked) => {
    try {
      await axios.post(`${API}/checklist/check`, {
        camper_id: camperId,
        is_checked: isChecked,
        checked_by: role,
        date: dateFromState || undefined
      });
      
      // Update local state
      setChecklist(prev => ({
        ...prev,
        [camperId]: { ...prev[camperId], is_checked: isChecked }
      }));
      
      setCampers(prev => prev.map(item => 
        item.camper_id === camperId 
          ? { ...item, is_checked: isChecked }
          : item
      ));
      
      toast.success(isChecked ? "Camper checked" : "Camper unchecked");
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  const checkedCount = campers.filter(c => c.is_checked).length;
  const allergyCampers = campers.filter(c => c.camper?.allergen_tags?.length > 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-[#2851A3]" size={32} />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 pb-24 md:pb-6" data-testid="group-detail-page">
      {/* Header with back button */}
      <div className="mb-6">
        <Button 
          variant="ghost" 
          onClick={handleBack}
          className="mb-4 text-slate-600 hover:text-[#2851A3]"
          data-testid="back-button"
        >
          <ChevronLeft size={20} className="mr-1" />
          Back to Lunch Periods
        </Button>
        
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#2851A3]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Users className="inline mr-2" size={28} />
              {groupData?.name || groupName || 'Group'}
            </h1>
            {dateFromState && (
              <p className="text-slate-500 mt-1 text-sm">Roster for {dateFromState}</p>
            )}
          </div>
          
          {/* Stats */}
          <div className="flex items-center gap-4">
            <div className="bg-slate-100 rounded-lg px-4 py-2 text-center">
              <div className="text-2xl font-bold text-[#2851A3]">{campers.length}</div>
              <div className="text-xs text-slate-500">Campers</div>
            </div>
            <div className={`rounded-lg px-4 py-2 text-center ${checkedCount === campers.length ? 'bg-green-100' : 'bg-yellow-100'}`}>
              <div className={`text-2xl font-bold ${checkedCount === campers.length ? 'text-green-600' : 'text-yellow-600'}`}>
                {checkedCount}/{campers.length}
              </div>
              <div className="text-xs text-slate-500">Checked</div>
            </div>
          </div>
        </div>
      </div>

      {/* Allergy Alert */}
      {allergyCampers.length > 0 && (
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-2 text-red-700 font-semibold mb-2">
            <ShieldAlert size={20} />
            {allergyCampers.length} Camper{allergyCampers.length !== 1 ? 's' : ''} with Allergies
          </div>
          <div className="flex flex-wrap gap-2">
            {allergyCampers.map(c => (
              <span key={c.camper_id} className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm">
                {c.camper?.first_name} {c.camper?.last_name}: {c.camper?.allergen_tags?.join(', ')}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Check All Button */}
      {campers.length > 0 && checkedCount < campers.length && (
        <div className="mb-4">
          <Button 
            onClick={async () => {
              for (const camper of campers.filter(c => !c.is_checked)) {
                await handleCheck(camper.camper_id, true);
              }
            }}
            className="btn-primary w-full sm:w-auto"
            data-testid="check-all-button"
          >
            <CheckCircle2 size={18} className="mr-2" />
            Check All {campers.length - checkedCount} Remaining
          </Button>
        </div>
      )}

      {/* Camper List */}
      <div className="space-y-3" data-testid="group-camper-list">
        {campers.map(item => (
          <CamperCard 
            key={item.camper_id}
            camper={item.camper}
            checklistItem={item}
            onCheck={handleCheck}
            onClick={(camper) => navigate(`/camper/${camper.id}`)}
          />
        ))}
      </div>

      {campers.length === 0 && (
        <div className="text-center py-12">
          <Users size={48} className="mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500">No campers in this group</p>
        </div>
      )}
    </div>
  );
};

// ============ ALLERGENS PAGE ============
const AllergensPage = () => {
  const { allergens, groups } = useApp();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAllergen, setSelectedAllergen] = useState(null);
  const [campers, setCampers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [groupFilter, setGroupFilter] = useState("");
  const navigate = useNavigate();

  // Restore scroll position on mount
  useEffect(() => {
    const savedScroll = sessionStorage.getItem('allergensPageScroll');
    if (savedScroll) {
      setTimeout(() => {
        window.scrollTo(0, parseInt(savedScroll, 10));
        sessionStorage.removeItem('allergensPageScroll');
      }, 100);
    }
  }, []);

  const handleCamperClick = (camper) => {
    sessionStorage.setItem('allergensPageScroll', window.scrollY.toString());
    navigate(`/camper/${camper.id}`);
  };

  const filteredAllergens = allergens.filter(a => 
    a.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const fetchCampersByAllergen = async (allergen) => {
    setLoading(true);
    setSelectedAllergen(allergen);
    try {
      const url = groupFilter 
        ? `${API}/allergens/${allergen.id}/campers?group_id=${groupFilter}`
        : `${API}/allergens/${allergen.id}/campers`;
      const res = await axios.get(url);
      setCampers(res.data.campers);
    } catch (e) {
      console.error("Failed to fetch campers", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedAllergen) {
      fetchCampersByAllergen(selectedAllergen);
    }
  }, [groupFilter]);

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 pb-24 md:pb-6" data-testid="allergens-page">
      <h1 className="text-2xl md:text-3xl font-bold text-[#2851A3] mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
        <ShieldAlert className="inline mr-2" size={28} />
        Allergens
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Allergen List */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input 
                placeholder="Search allergens..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-allergen-input"
              />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[500px]">
              <div className="space-y-1 p-2">
                {filteredAllergens.map(a => (
                  <button
                    key={a.id}
                    onClick={() => fetchCampersByAllergen(a)}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-all hover:bg-slate-50 ${
                      selectedAllergen?.id === a.id ? 'bg-blue-50 border border-blue-200' : ''
                    }`}
                    data-testid={`allergen-item-${a.id}`}
                  >
                    <span className="font-medium text-slate-700">{a.name}</span>
                  </button>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Campers with selected allergen */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <CardTitle className="text-[#2851A3]">
                  {selectedAllergen ? selectedAllergen.name : "Select an Allergen"}
                </CardTitle>
                {selectedAllergen && (
                  <CardDescription>{campers.length} camper{campers.length !== 1 ? 's' : ''}</CardDescription>
                )}
              </div>
              {selectedAllergen && (
                <Select value={groupFilter || "all"} onValueChange={(v) => setGroupFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-[180px]" data-testid="allergen-group-filter">
                    <SelectValue placeholder="All Groups" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Groups</SelectItem>
                    {groups.map(g => (
                      <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <RefreshCw className="animate-spin text-[#2851A3]" size={24} />
              </div>
            ) : selectedAllergen ? (
              <div className="space-y-3">
                {campers.map(camper => (
                  <div 
                    key={camper.id}
                    onClick={() => handleCamperClick(camper)}
                    className="card-hover p-4 bg-white border rounded-xl flex items-center justify-between"
                    data-testid={`allergen-camper-${camper.id}`}
                  >
                    <div>
                      <h3 className="font-semibold text-slate-800">
                        {camper.first_name} {camper.last_name}
                      </h3>
                      <span className="group-badge text-xs">{camper.group_name}</span>
                    </div>
                    <ChevronRight size={20} className="text-slate-300" />
                  </div>
                ))}
                {campers.length === 0 && (
                  <p className="text-center text-slate-500 py-8">No campers with this allergen</p>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <ShieldAlert size={48} className="mx-auto text-slate-200 mb-4" />
                <p className="text-slate-400">Select an allergen to view campers</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ============ GROUPS PAGE ============
const GroupsPage = () => {
  const { groups, allergens } = useApp();
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [campers, setCampers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allergenFilter, setAllergenFilter] = useState("");
  const navigate = useNavigate();

  // Restore scroll position on mount
  useEffect(() => {
    const savedScroll = sessionStorage.getItem('groupsPageScroll');
    if (savedScroll) {
      setTimeout(() => {
        window.scrollTo(0, parseInt(savedScroll, 10));
        sessionStorage.removeItem('groupsPageScroll');
      }, 100);
    }
  }, []);

  const handleCamperClick = (camper) => {
    sessionStorage.setItem('groupsPageScroll', window.scrollY.toString());
    navigate(`/camper/${camper.id}`);
  };

  const fetchCampersByGroup = async (group) => {
    setLoading(true);
    setSelectedGroup(group);
    try {
      let url = `${API}/campers?group_id=${group.id}`;
      if (allergenFilter) {
        url += `&allergen=${encodeURIComponent(allergenFilter)}`;
      }
      const res = await axios.get(url);
      setCampers(res.data);
    } catch (e) {
      console.error("Failed to fetch campers", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedGroup) {
      fetchCampersByGroup(selectedGroup);
    }
  }, [allergenFilter]);

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 pb-24 md:pb-6" data-testid="groups-page">
      <h1 className="text-2xl md:text-3xl font-bold text-[#2851A3] mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
        <Users className="inline mr-2" size={28} />
        Groups
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Group List */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Select Group</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[500px]">
              <div className="space-y-1 p-2">
                {groups.map(g => (
                  <button
                    key={g.id}
                    onClick={() => fetchCampersByGroup(g)}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-all hover:bg-slate-50 ${
                      selectedGroup?.id === g.id ? 'bg-blue-50 border border-blue-200' : ''
                    }`}
                    data-testid={`group-item-${g.id}`}
                  >
                    <span className="font-medium text-slate-700">{g.name}</span>
                    {g.counselor_name && (
                      <p className="text-xs text-slate-400 mt-0.5">{g.counselor_name}</p>
                    )}
                  </button>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Campers in selected group */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <CardTitle className="text-[#2851A3]">
                  {selectedGroup ? selectedGroup.name : "Select a Group"}
                </CardTitle>
                {selectedGroup && (
                  <CardDescription>
                    {campers.length} camper{campers.length !== 1 ? 's' : ''}
                    {selectedGroup.counselor_name && ` • ${selectedGroup.counselor_name}`}
                  </CardDescription>
                )}
              </div>
              {selectedGroup && (
                <Select value={allergenFilter || "all"} onValueChange={(v) => setAllergenFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-[180px]" data-testid="group-allergen-filter">
                    <SelectValue placeholder="All Allergens" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Allergens</SelectItem>
                    {allergens.slice(0, 20).map(a => (
                      <SelectItem key={a.id} value={a.name}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <RefreshCw className="animate-spin text-[#2851A3]" size={24} />
              </div>
            ) : selectedGroup ? (
              <div className="space-y-3">
                {campers.map(camper => (
                  <div 
                    key={camper.id}
                    onClick={() => handleCamperClick(camper)}
                    className="card-hover p-4 bg-white border rounded-xl"
                    data-testid={`group-camper-${camper.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-800">
                            {camper.first_name} {camper.last_name}
                          </h3>
                          {camper.is_cross_contamination_sensitive && (
                            <span className="allergy-chip allergy-chip-cc text-xs">CC</span>
                          )}
                        </div>
                        {camper.allergen_tags?.length > 0 ? (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {camper.allergen_tags.slice(0, 3).map((a, i) => (
                              <AllergyChip key={i} allergen={a} />
                            ))}
                            {camper.allergen_tags.length > 3 && (
                              <span className="text-xs text-slate-400 self-center">+{camper.allergen_tags.length - 3}</span>
                            )}
                          </div>
                        ) : (
                          <span className="allergy-chip allergy-chip-safe mt-2 text-xs">
                            <CheckCircle2 size={10} className="mr-1" /> NKDA
                          </span>
                        )}
                      </div>
                      <ChevronRight size={20} className="text-slate-300" />
                    </div>
                  </div>
                ))}
                {campers.length === 0 && (
                  <p className="text-center text-slate-500 py-8">No campers in this group</p>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <Users size={48} className="mx-auto text-slate-200 mb-4" />
                <p className="text-slate-400">Select a group to view campers</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ============ CAMPER DETAIL PAGE ============
const CamperDetailPage = () => {
  const { role, config, selectedDate } = useApp();
  const [camper, setCamper] = useState(null);
  const [checklistItem, setChecklistItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [staffNote, setStaffNote] = useState("");
  const navigate = useNavigate();

  // Get camper ID from URL
  const camperId = window.location.pathname.split('/').pop();

  useEffect(() => {
    const fetchCamper = async () => {
      try {
        const dateParam = selectedDate ? `?date=${selectedDate}` : '';
        const [camperRes, checklistRes] = await Promise.all([
          axios.get(`${API}/campers/${camperId}`),
          axios.get(`${API}/checklist/today${dateParam}`)
        ]);
        setCamper(camperRes.data);
        const item = checklistRes.data.items.find(i => i.camper_id === camperId);
        setChecklistItem(item);
        setStaffNote(item?.staff_note || "");
      } catch (e) {
        console.error("Failed to fetch camper", e);
      } finally {
        setLoading(false);
      }
    };
    fetchCamper();
  }, [camperId, selectedDate]);

  const handleCheck = async (isChecked) => {
    try {
      await axios.post(`${API}/checklist/check`, {
        camper_id: camperId,
        is_checked: isChecked,
        checked_by: role,
        staff_note: staffNote,
        date: selectedDate || undefined
      });
      setChecklistItem(prev => ({ ...prev, is_checked: isChecked, checked_by: role }));
      toast.success(isChecked ? "Camper checked" : "Camper unchecked");
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const highlightCCKeywords = (text) => {
    if (!text || !config.cross_contamination_keywords) return text;
    let result = text;
    config.cross_contamination_keywords.forEach(kw => {
      const regex = new RegExp(`(${kw})`, 'gi');
      result = result.replace(regex, '<mark class="cc-highlight">$1</mark>');
    });
    return result;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-[#2851A3]" size={32} />
      </div>
    );
  }

  if (!camper) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center">
        <p className="text-slate-500">Camper not found</p>
        <Button onClick={() => navigate(-1)} className="mt-4">Go Back</Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-4 md:p-6 pb-24 md:pb-6" data-testid="camper-detail-page">
      {/* Back button */}
      <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
        ← Back
      </Button>

      {/* Header Card */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-[#2851A3]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {camper.first_name} {camper.last_name}
              </h1>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span className="group-badge">{camper.group_name}</span>
                {camper.counselor_name && (
                  <span className="text-sm text-slate-500">• {camper.counselor_name}</span>
                )}
                {camper.is_cross_contamination_sensitive && (
                  <span className="allergy-chip allergy-chip-cc">
                    <AlertTriangle size={12} className="mr-1" /> Cross-Contamination Sensitive
                  </span>
                )}
              </div>
            </div>

            {/* Large Checkbox */}
            <div className="flex flex-col items-center gap-2">
              <Checkbox 
                checked={checklistItem?.is_checked || false}
                onCheckedChange={handleCheck}
                className="checkbox-lg"
                data-testid="camper-detail-checkbox"
              />
              <span className={`text-xs font-medium ${checklistItem?.is_checked ? 'text-emerald-600' : 'text-slate-400'}`}>
                {checklistItem?.is_checked ? 'Checked' : 'Unchecked'}
              </span>
            </div>
          </div>

          {/* Today's Status */}
          {checklistItem?.is_checked && (
            <div className="mt-4 p-3 bg-emerald-50 rounded-lg border border-emerald-100">
              <div className="flex items-center gap-2 text-emerald-700">
                <CheckCircle2 size={18} />
                <span className="font-medium">Checked for today</span>
              </div>
              <p className="text-sm text-emerald-600 mt-1">
                By {checklistItem.checked_by} at {checklistItem.checked_at ? new Date(checklistItem.checked_at).toLocaleTimeString() : 'N/A'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Allergies */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[#2851A3]">
            <ShieldAlert size={20} /> Allergies
          </CardTitle>
        </CardHeader>
        <CardContent>
          {camper.allergen_tags?.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {camper.allergen_tags.map((a, i) => (
                <AllergyChip key={i} allergen={a} size="lg" />
              ))}
            </div>
          ) : (
            <span className="allergy-chip allergy-chip-safe">
              <CheckCircle2 size={14} className="mr-1" /> No Known Allergies (NKDA)
            </span>
          )}
          {/* Raw allergy details */}
          {(camper.food_allergies_raw || camper.other_allergies) && (
            <div className="mt-4 space-y-2">
              {camper.food_allergies_raw && (
                <div className="text-sm">
                  <span className="text-slate-500 font-medium">Food Allergies: </span>
                  <span className="text-slate-700">{camper.food_allergies_raw}</span>
                </div>
              )}
              {camper.other_allergies && (
                <div className="text-sm">
                  <span className="text-slate-500 font-medium">Other Allergies: </span>
                  <span className="text-slate-700">{camper.other_allergies}</span>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lunch Order */}
      {checklistItem?.lunch_order && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#2851A3]">
              <Utensils size={20} /> Lunch Order
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 text-lg font-medium text-slate-800">
              {checklistItem.lunch_order}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Session Info */}
      {camper.enrolled_session && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#2851A3]">
              <Calendar size={20} /> Session
            </CardTitle>
          </CardHeader>
          <CardContent>
            <span className="text-slate-700">{camper.enrolled_session}</span>
          </CardContent>
        </Card>
      )}

      {/* Notes Section */}
      {(camper.dietary_detail || camper.pip_allergies_food || camper.pip_allergies_reaction) && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-[#2851A3]">Notes & Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {camper.dietary_detail && (
              <div>
                <Label className="text-slate-500 text-xs uppercase tracking-wider">Dietary Details</Label>
                <p 
                  className="text-slate-700 mt-1"
                  dangerouslySetInnerHTML={{ __html: highlightCCKeywords(camper.dietary_detail) }}
                />
              </div>
            )}
            {camper.pip_allergies_food && (
              <div>
                <Label className="text-slate-500 text-xs uppercase tracking-wider">PIP Allergies (Food)</Label>
                <p 
                  className="text-slate-700 mt-1"
                  dangerouslySetInnerHTML={{ __html: highlightCCKeywords(camper.pip_allergies_food) }}
                />
              </div>
            )}
            {camper.pip_allergies_reaction && (
              <div>
                <Label className="text-slate-500 text-xs uppercase tracking-wider">Reaction Info</Label>
                <p className="text-slate-700 mt-1">{camper.pip_allergies_reaction}</p>
              </div>
            )}
            {camper.dietary_needs && (
              <div>
                <Label className="text-slate-500 text-xs uppercase tracking-wider">Dietary Needs</Label>
                <p className="text-slate-700 mt-1">{camper.dietary_needs}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Guardian Contact */}
      {camper.guardian_emails?.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#2851A3]">
              <Mail size={20} /> Guardian Contact
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {camper.guardian_emails.map((email, i) => (
                <a 
                  key={i}
                  href={`mailto:${email}`}
                  className="flex items-center gap-2 text-blue-600 hover:underline"
                  data-testid={`guardian-email-${i}`}
                >
                  <Mail size={16} />
                  {email}
                </a>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Staff Note (Nurse role) */}
      {(role === "nurse" || role === "admin") && (
        <Card>
          <CardHeader>
            <CardTitle className="text-[#2851A3]">Staff Note</CardTitle>
            <CardDescription>Add a note visible to staff for today</CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea 
              value={staffNote}
              onChange={(e) => setStaffNote(e.target.value)}
              placeholder="Add a note..."
              className="mb-3"
              data-testid="staff-note-input"
            />
            <Button 
              onClick={() => handleCheck(checklistItem?.is_checked || false)}
              className="btn-primary"
              data-testid="save-note-btn"
            >
              Save Note
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ============ ADMIN PAGE ============
const AdminPage = () => {
  const { groups, allergens, config, fetchGroups, fetchConfig, seedDatabase } = useApp();
  const [activeTab, setActiveTab] = useState("groups");
  const [editGroup, setEditGroup] = useState(null);
  const [editCamper, setEditCamper] = useState(null);
  const [campers, setCampers] = useState([]);
  const [keywords, setKeywords] = useState(config.cross_contamination_keywords?.join('\n') || '');

  useEffect(() => {
    fetchCampers();
  }, []);

  useEffect(() => {
    setKeywords(config.cross_contamination_keywords?.join('\n') || '');
  }, [config]);

  const fetchCampers = async () => {
    try {
      const res = await axios.get(`${API}/campers`);
      setCampers(res.data);
    } catch (e) {
      console.error("Failed to fetch campers", e);
    }
  };

  const handleSaveGroup = async () => {
    try {
      if (editGroup.id) {
        await axios.put(`${API}/groups/${editGroup.id}`, editGroup);
        toast.success("Group updated");
      } else {
        await axios.post(`${API}/groups`, editGroup);
        toast.success("Group created");
      }
      setEditGroup(null);
      fetchGroups();
    } catch (e) {
      toast.error("Failed to save group");
    }
  };

  const handleDeleteGroup = async (id) => {
    try {
      await axios.delete(`${API}/groups/${id}`);
      toast.success("Group deleted");
      fetchGroups();
    } catch (e) {
      toast.error("Failed to delete group");
    }
  };

  const handleSaveKeywords = async () => {
    try {
      const keywordList = keywords.split('\n').map(k => k.trim()).filter(k => k);
      await axios.put(`${API}/config/keywords`, keywordList);
      toast.success("Keywords updated");
      fetchConfig();
    } catch (e) {
      toast.error("Failed to update keywords");
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 pb-24 md:pb-6" data-testid="admin-page">
      <h1 className="text-2xl md:text-3xl font-bold text-[#2851A3] mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
        <Settings className="inline mr-2" size={28} />
        Admin Settings
      </h1>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="groups" data-testid="tab-groups">Groups</TabsTrigger>
          <TabsTrigger value="campers" data-testid="tab-campers">Campers</TabsTrigger>
          <TabsTrigger value="keywords" data-testid="tab-keywords">CC Keywords</TabsTrigger>
          <TabsTrigger value="data" data-testid="tab-data">Data</TabsTrigger>
        </TabsList>

        {/* Groups Tab */}
        <TabsContent value="groups">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Groups ({groups.length})</CardTitle>
              <Button onClick={() => setEditGroup({ name: '', counselor_name: '', counselor_email: '' })} className="btn-primary" data-testid="add-group-btn">
                Add Group
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Counselor</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groups.map(g => (
                    <TableRow key={g.id}>
                      <TableCell className="font-medium">{g.name}</TableCell>
                      <TableCell>{g.counselor_name || '-'}</TableCell>
                      <TableCell>{g.counselor_email || '-'}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => setEditGroup(g)}>Edit</Button>
                        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteGroup(g.id)}>Delete</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Campers Tab */}
        <TabsContent value="campers">
          <Card>
            <CardHeader>
              <CardTitle>Campers ({campers.length})</CardTitle>
              <CardDescription>Manage camper records</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Group</TableHead>
                    <TableHead>Allergies</TableHead>
                    <TableHead>CC</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campers.slice(0, 50).map(c => (
                    <TableRow key={c.id}>
                      <TableCell className="font-medium">{c.first_name} {c.last_name}</TableCell>
                      <TableCell><span className="group-badge">{c.group_name}</span></TableCell>
                      <TableCell>
                        {c.allergen_tags?.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {c.allergen_tags.slice(0, 2).map((a, i) => (
                              <span key={i} className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">{a}</span>
                            ))}
                            {c.allergen_tags.length > 2 && <span className="text-xs text-slate-400">+{c.allergen_tags.length - 2}</span>}
                          </div>
                        ) : (
                          <span className="text-xs text-emerald-600">NKDA</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {c.is_cross_contamination_sensitive && (
                          <AlertTriangle size={16} className="text-orange-500" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {campers.length > 50 && (
                <p className="text-sm text-slate-500 mt-4 text-center">Showing 50 of {campers.length} campers</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Keywords Tab */}
        <TabsContent value="keywords">
          <Card>
            <CardHeader>
              <CardTitle>Cross-Contamination Keywords</CardTitle>
              <CardDescription>
                Campers with these keywords in their notes will be flagged as cross-contamination sensitive
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea 
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                placeholder="Enter keywords, one per line..."
                className="min-h-[200px] font-mono text-sm"
                data-testid="keywords-textarea"
              />
              <Button onClick={handleSaveKeywords} className="mt-4 btn-primary" data-testid="save-keywords-btn">
                Save Keywords
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Data Tab */}
        <TabsContent value="data">
          <Card>
            <CardHeader>
              <CardTitle>Data Management</CardTitle>
              <CardDescription>Seed or reset the database</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <h3 className="font-medium text-slate-800 mb-2">Seed Demo Data</h3>
                <p className="text-sm text-slate-500 mb-4">
                  Populate the database with sample groups, campers, and allergens for testing.
                </p>
                <Button onClick={seedDatabase} className="btn-primary" data-testid="seed-data-btn">
                  Seed Database
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Group Dialog */}
      <Dialog open={!!editGroup} onOpenChange={() => setEditGroup(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editGroup?.id ? 'Edit Group' : 'Add Group'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Group Name</Label>
              <Input 
                value={editGroup?.name || ''}
                onChange={(e) => setEditGroup({ ...editGroup, name: e.target.value })}
                placeholder="e.g., B01 Lobsters"
                data-testid="group-name-input"
              />
            </div>
            <div>
              <Label>Counselor Name</Label>
              <Input 
                value={editGroup?.counselor_name || ''}
                onChange={(e) => setEditGroup({ ...editGroup, counselor_name: e.target.value })}
                placeholder="e.g., Sarah Johnson"
                data-testid="counselor-name-input"
              />
            </div>
            <div>
              <Label>Counselor Email</Label>
              <Input 
                value={editGroup?.counselor_email || ''}
                onChange={(e) => setEditGroup({ ...editGroup, counselor_email: e.target.value })}
                placeholder="e.g., sjohnson@camp.com"
                data-testid="counselor-email-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditGroup(null)}>Cancel</Button>
            <Button onClick={handleSaveGroup} className="btn-primary" data-testid="save-group-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============ AUDIT LOGS PAGE ============
const AuditLogsPage = () => {
  const { groups, stats } = useApp();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState(stats.today_date || '');
  const [groupFilter, setGroupFilter] = useState("");

  const fetchLogs = async () => {
    setLoading(true);
    try {
      let url = `${API}/audit-logs?limit=200`;
      if (dateFilter) url += `&date=${dateFilter}`;
      if (groupFilter) url += `&group_id=${groupFilter}`;
      const res = await axios.get(url);
      setLogs(res.data);
    } catch (e) {
      console.error("Failed to fetch logs", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [dateFilter, groupFilter]);

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 pb-24 md:pb-6" data-testid="audit-logs-page">
      <h1 className="text-2xl md:text-3xl font-bold text-[#2851A3] mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
        <FileText className="inline mr-2" size={28} />
        Audit Logs
      </h1>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap gap-4">
            <div>
              <Label className="text-xs text-slate-500">Date</Label>
              <Input 
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-[160px]"
                data-testid="audit-date-filter"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Group</Label>
              <Select value={groupFilter || "all"} onValueChange={(v) => setGroupFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[180px]" data-testid="audit-group-filter">
                  <SelectValue placeholder="All Groups" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Groups</SelectItem>
                  {groups.map(g => (
                    <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <RefreshCw className="animate-spin text-[#2851A3]" size={24} />
            </div>
          ) : logs.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Camper</TableHead>
                  <TableHead>Group</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>By</TableHead>
                  <TableHead>Note</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map(log => (
                  <TableRow key={log.id}>
                    <TableCell className="text-sm">
                      <div>{log.date}</div>
                      <div className="text-xs text-slate-400">
                        {new Date(log.timestamp).toLocaleTimeString()}
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{log.camper_name}</TableCell>
                    <TableCell><span className="group-badge text-xs">{log.group_name}</span></TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        log.action === 'checked' ? 'bg-emerald-100 text-emerald-700' :
                        log.action === 'unchecked' ? 'bg-slate-100 text-slate-600' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        {log.action}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{log.user}</TableCell>
                    <TableCell className="text-sm text-slate-500 max-w-[200px] truncate">
                      {log.details || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <FileText size={48} className="mx-auto text-slate-200 mb-4" />
              <p className="text-slate-400">No audit logs found</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ============ MASTER ALLERGY REPORT (PRINTABLE) ============
const MasterAllergyReport = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterAllergiesOnly, setFilterAllergiesOnly] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const res = await axios.get(`${API}/report/master-allergy-list`);
        setReport(res.data);
      } catch (e) {
        toast.error("Failed to load report");
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-[#2851A3]" size={32} />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center">
        <p className="text-slate-500">No report data. Sync a roster first.</p>
        <Button onClick={() => navigate("/")} className="mt-4">Go to Today</Button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6 pb-24 md:pb-6" data-testid="master-report-page">
      {/* Screen-only controls */}
      <div className="print:hidden mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <Button variant="ghost" onClick={() => navigate(-1)} className="mb-2">
              <ChevronLeft size={18} className="mr-1" /> Back
            </Button>
            <h1 className="text-2xl md:text-3xl font-bold text-[#2851A3]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <FileText className="inline mr-2" size={28} />
              Master Allergy Report
            </h1>
            <p className="text-slate-500 mt-1">
              {report.total_campers} campers — {report.total_with_allergies} with allergies
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Switch
                checked={filterAllergiesOnly}
                onCheckedChange={setFilterAllergiesOnly}
                data-testid="filter-allergies-toggle"
              />
              <Label className="text-sm text-slate-600">Allergies only</Label>
            </div>
            <Button onClick={() => window.print()} className="btn-primary" data-testid="print-report-btn">
              <Printer size={16} className="mr-2" /> Print Report
            </Button>
          </div>
        </div>
      </div>

      {/* Print header */}
      <div className="hidden print:block mb-4">
        <h1 className="text-xl font-bold text-center">Rolling River Day Camp — Master Allergy Report</h1>
        <p className="text-center text-sm text-gray-500">
          Generated {new Date(report.generated_at).toLocaleString()} — {report.total_campers} campers, {report.total_with_allergies} with allergies
        </p>
      </div>

      {/* Report body */}
      {report.report.map((group, idx) => {
        const visibleCampers = filterAllergiesOnly
          ? group.campers.filter(c => c.allergen_tags.length > 0)
          : group.campers;
        if (visibleCampers.length === 0) return null;

        return (
          <div key={group.group_name} className={`border border-slate-200 rounded-lg overflow-hidden mb-6 ${idx > 0 ? 'break-before-page' : ''}`}>
            <div className="bg-[#2851A3] text-white px-4 py-2 flex items-center justify-between">
              <span className="font-bold text-lg">{group.group_name}</span>
              <span className="text-sm opacity-80">
                Lunch: {group.lunch_period} — {visibleCampers.length} camper{visibleCampers.length !== 1 ? 's' : ''}
                {group.allergy_count > 0 && ` • ${group.allergy_count} with allergies`}
              </span>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left px-4 py-1.5 text-xs text-slate-500 font-medium w-[200px]">Name</th>
                  <th className="text-left px-4 py-1.5 text-xs text-slate-500 font-medium">Food Allergies</th>
                  <th className="text-left px-4 py-1.5 text-xs text-slate-500 font-medium">Other Allergies</th>
                  <th className="text-left px-4 py-1.5 text-xs text-slate-500 font-medium w-[120px]">Session</th>
                </tr>
              </thead>
              <tbody>
                {visibleCampers.map((camper) => (
                  <tr key={camper.id} className={`border-b border-slate-50 ${camper.allergen_tags.length > 0 ? 'bg-red-50/50 print:bg-gray-50' : ''} ${camper.is_cross_contamination_sensitive ? 'border-l-2 border-l-orange-400' : ''}`}>
                    <td className="px-4 py-1.5 font-medium text-slate-800">
                      {camper.last_name}, {camper.first_name}
                      {camper.is_cross_contamination_sensitive && (
                        <span className="ml-1 text-[10px] bg-orange-100 text-orange-700 px-1 rounded font-bold print:border print:border-gray-400">CC</span>
                      )}
                    </td>
                    <td className="px-4 py-1.5">
                      {camper.allergen_tags.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {camper.allergen_tags.map((tag, i) => (
                            <span key={i} className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded print:border print:border-gray-400 print:bg-gray-100">
                              {tag}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-1.5 text-xs text-slate-600">
                      {camper.other_allergies || <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-1.5 text-xs text-slate-500">{camper.enrolled_session || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
};

// ============ PASSWORD GATE ============
const APP_KEY = btoa("Camp1993");

const PasswordGate = ({ children }) => {
  const [authenticated, setAuthenticated] = useState(() => {
    return sessionStorage.getItem("rrdc_allergen_auth") === "true";
  });
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (btoa(password) === APP_KEY) {
      sessionStorage.setItem("rrdc_allergen_auth", "true");
      setAuthenticated(true);
      setError("");
    } else {
      setError("Incorrect password. Please try again.");
      setPassword("");
    }
  };

  if (authenticated) return children;

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="password-gate">
      <div className="w-full max-w-sm">
        <div className="bg-white rounded-2xl border shadow-sm p-8 text-center">
          <img
            src="/Riverview Athletics Logo.png"
            alt="Rolling River"
            className="h-16 w-16 mx-auto mb-4 rounded-full object-cover"
          />
          <h1 className="text-xl font-bold text-[#2851A3] mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Rolling River
          </h1>
          <p className="text-slate-500 text-sm mb-6">Allergy Check</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              type="password"
              placeholder="Enter password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(""); }}
              autoFocus
              className="text-center"
              data-testid="password-input"
            />
            {error && (
              <p className="text-sm text-red-600" data-testid="password-error">{error}</p>
            )}
            <Button type="submit" className="w-full btn-primary" data-testid="password-submit">
              Enter
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

// ============ MAIN APP ============
function App() {
  return (
    <PasswordGate>
      <AppProvider>
        <div className="min-h-screen bg-slate-50">
          <Toaster position="top-center" richColors />
          <BrowserRouter>
            <Header />
            <main className="pb-20 md:pb-0">
              <Routes>
                <Route path="/" element={<TodayPage />} />
                <Route path="/allergens" element={<AllergensPage />} />
                <Route path="/groups" element={<GroupsPage />} />
                <Route path="/group/:id" element={<GroupDetailPage />} />
                <Route path="/camper/:id" element={<CamperDetailPage />} />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/audit" element={<AuditLogsPage />} />
                <Route path="/report" element={<MasterAllergyReport />} />
              </Routes>
            </main>
            <MobileNav />
          </BrowserRouter>
        </div>
      </AppProvider>
    </PasswordGate>
  );
}

export default App;
